"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { 
  Building, 
  Search, 
  Filter, 
  Calendar, 
  DollarSign, 
  CheckCircle, 
  Users, 
  Car, 
  ArrowLeft,
  RotateCcw,
  Sparkles,
  TrendingUp
} from "lucide-react";
import { RealEstateLead } from "@/lib/demos/realEstateDb";
import RealEstateKanban from "./RealEstateKanban";
import RealEstateMobilePipeline from "./RealEstateMobilePipeline";
import RealEstateLeadDetailModal from "./RealEstateLeadDetailModal";
import RealEstateStageModal from "./RealEstateStageModal";

interface RealEstateCrmDashboardProps {
  currency?: "INR" | "USD";
}

export default function RealEstateCrmDashboard({ currency = "INR" }: RealEstateCrmDashboardProps) {
  const [leads, setLeads] = useState<RealEstateLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [selectedLead, setSelectedLead] = useState<RealEstateLead | null>(null);
  const [stageModalData, setStageModalData] = useState<{
    lead: RealEstateLead;
    targetStage: "Site Visit Scheduled" | "Booking Token" | "Sale Deed (Won)";
  } | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.set("search", searchTerm);
      if (stageFilter !== "ALL") params.set("stage", stageFilter);
      if (typeFilter !== "ALL") params.set("property_type", typeFilter);

      const res = await fetch(`/api/demos/real-estate/leads?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
      }
    } catch (err) {
      console.error("Error loading Real Estate CRM leads:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [searchTerm, stageFilter, typeFilter]);

  const handleUpdateStage = async (
    leadId: number, 
    newStage: RealEstateLead["stage"],
    customPayload?: {
      site_visit_date?: string;
      site_visit_pickup?: number;
      options?: any;
    }
  ) => {
    const targetLead = leads.find((l) => l.id === leadId);
    if (!targetLead) return;

    // Check if manual entry is required and not yet provided
    if (
      !customPayload && 
      (newStage === "Site Visit Scheduled" || newStage === "Booking Token" || newStage === "Sale Deed (Won)")
    ) {
      setStageModalData({
        lead: targetLead,
        targetStage: newStage,
      });
      return;
    }

    // Optimistic UI update
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { 
        ...l, 
        stage: newStage,
        ...(customPayload?.site_visit_date ? { site_visit_date: customPayload.site_visit_date } : {}),
        ...(customPayload?.site_visit_pickup !== undefined ? { site_visit_pickup: customPayload.site_visit_pickup } : {})
      } : l))
    );

    try {
      const res = await fetch(`/api/demos/real-estate/leads/${leadId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          stage: newStage,
          ...(customPayload || {})
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setToastMessage(`⚡ Stage Automation: Email sent to ${targetLead.email}`);
        setTimeout(() => setToastMessage(null), 5000);
        fetchLeads();
      }
    } catch (err) {
      console.error("Failed to update stage:", err);
      fetchLeads();
    }
  };


  // KPIs
  const totalActive = leads.filter((l) => l.stage !== "Lost");
  const siteVisitsScheduled = leads.filter((l) => l.stage === "Site Visit Scheduled" || l.stage === "Site Visit Completed");
  const bookingTokens = leads.filter((l) => l.stage === "Booking Token");
  const closedDeals = leads.filter((l) => l.stage === "Sale Deed (Won)");

  // Sum numeric pipeline
  const pipelineINR = totalActive
    .filter((l) => l.currency === "INR")
    .reduce((sum, l) => sum + (l.budget_numeric || 0), 0);
  const pipelineUSD = totalActive
    .filter((l) => l.currency === "USD")
    .reduce((sum, l) => sum + (l.budget_numeric || 0), 0);

  const wonINR = closedDeals
    .filter((l) => l.currency === "INR")
    .reduce((sum, l) => sum + (l.budget_numeric || 0), 0);
  const wonUSD = closedDeals
    .filter((l) => l.currency === "USD")
    .reduce((sum, l) => sum + (l.budget_numeric || 0), 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Executive Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20">
                Demo Sales Pipeline
              </span>
              <span className="text-xs text-slate-400">Aura Prime Luxury Estates CRM</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-heading">
              Real Estate Deal Pipeline &amp; Site Visits
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <Link
              href="/demos/real-estate"
              className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition-colors"
            >
              <span>View Client Website</span>
              <span className="text-amber-400">→</span>
            </Link>
          </div>
        </div>

        {/* 4 Executive KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Active Buyers */}
          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-sm">
            <div className="flex items-center justify-between text-slate-400 text-xs font-semibold mb-1">
              <span>Active Prospects</span>
              <Users className="w-4 h-4 text-blue-400" />
            </div>
            <div className="text-2xl font-bold text-white">
              {totalActive.length} <span className="text-xs text-slate-500 font-normal">HNIs</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Qualified buyer pipeline</p>
          </div>

          {/* Site Visits Scheduled */}
          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-sm">
            <div className="flex items-center justify-between text-slate-400 text-xs font-semibold mb-1">
              <span>Site Inspections</span>
              <Calendar className="w-4 h-4 text-amber-400" />
            </div>
            <div className="text-2xl font-bold text-amber-400">
              {siteVisitsScheduled.length} <span className="text-xs text-slate-400 font-normal">Booked</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              {leads.filter((l) => l.site_visit_pickup === 1).length} with VIP Chauffeur
            </p>
          </div>

          {/* Gross Asset Pipeline (GAV) */}
          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-sm">
            <div className="flex items-center justify-between text-slate-400 text-xs font-semibold mb-1">
              <span>Gross Pipeline GAV</span>
              <TrendingUp className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="text-xl font-bold text-emerald-400 truncate">
              {pipelineINR > 0 ? `₹${(pipelineINR / 10000000).toFixed(1)} Cr` : ""}
              {pipelineINR > 0 && pipelineUSD > 0 ? " + " : ""}
              {pipelineUSD > 0 ? `$${(pipelineUSD / 1000000).toFixed(2)}M` : ""}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Total active deal valuation</p>
          </div>

          {/* Tokens & Executed Won */}
          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-sm">
            <div className="flex items-center justify-between text-slate-400 text-xs font-semibold mb-1">
              <span>Booking Tokens &amp; Won</span>
              <CheckCircle className="w-4 h-4 text-purple-400" />
            </div>
            <div className="text-xl font-bold text-purple-400 truncate">
              {wonINR > 0 || wonUSD > 0 ? (
                <>
                  {wonINR > 0 ? `₹${(wonINR / 10000000).toFixed(1)} Cr` : ""}
                  {wonUSD > 0 ? `$${(wonUSD / 1000000).toFixed(2)}M` : ""}
                </>
              ) : (
                `${bookingTokens.length} EOI Units`
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              {bookingTokens.length} in EOI • {closedDeals.length} Won Contracts
            </p>
          </div>
        </div>

        {/* Filter Bar - Desktop Only */}
        <div className="hidden md:flex p-4 rounded-2xl bg-slate-900/60 border border-slate-800/80 flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-3 flex-grow max-w-2xl">
            {/* Search */}
            <div className="relative flex-grow min-w-[200px]">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search buyer name, property, phone, or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none"
              />
            </div>

            {/* Stage filter */}
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-300 font-medium focus:border-amber-500 focus:outline-none"
            >
              <option value="ALL">All Stages</option>
              <option value="New Inquiries">New Inquiries</option>
              <option value="Site Visit Scheduled">Site Visit Scheduled</option>
              <option value="Site Visit Completed">Visit Completed</option>
              <option value="Booking Token">Booking Token (EOI)</option>
              <option value="Sale Deed (Won)">Sale Deed (Won)</option>
              <option value="Lost">Lost / Inactive</option>
            </select>

            {/* Type filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-300 font-medium focus:border-amber-500 focus:outline-none"
            >
              <option value="ALL">All Asset Types</option>
              <option value="Penthouse">Penthouses</option>
              <option value="Villa">Villas</option>
              <option value="Apartment">Apartments</option>
              <option value="Commercial">Commercial</option>
            </select>
          </div>

          <div className="text-xs text-slate-400 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>Interactive Drag &amp; Drop Active</span>
          </div>
        </div>

        {/* Desktop View: Multi-Column Kanban */}
        <div className="hidden md:block">
          <RealEstateKanban
            leads={leads}
            onSelectLead={(lead) => setSelectedLead(lead)}
            onUpdateStage={handleUpdateStage}
            currency={currency}
          />
        </div>

        {/* Mobile-First View: Fast Touch Stream with 1-Tap WhatsApp & Call */}
        <div className="block md:hidden">
          <RealEstateMobilePipeline
            leads={leads}
            onSelectLead={(lead) => setSelectedLead(lead)}
            onUpdateStage={handleUpdateStage}
            currency={currency}
          />
        </div>

        {/* Lead Detail Modal */}
        {selectedLead && (
          <RealEstateLeadDetailModal
            lead={selectedLead}
            currency={currency}
            onClose={() => setSelectedLead(null)}
            onLeadUpdated={fetchLeads}
          />
        )}

        {/* Stage Approval & Details Modal */}
        {stageModalData && (
          <RealEstateStageModal
            lead={stageModalData.lead}
            targetStage={stageModalData.targetStage}
            isOpen={true}
            onClose={() => setStageModalData(null)}
            onConfirm={async ({ stage, site_visit_date, site_visit_pickup, options }) => {
              await handleUpdateStage(stageModalData.lead.id, stage, {
                site_visit_date,
                site_visit_pickup,
                options,
              });
              setStageModalData(null);
            }}
          />
        )}

        {/* Live Stage Automation Toast Notification */}
        {toastMessage && (
          <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
            <div className="px-4 py-3 rounded-2xl bg-slate-900 border border-amber-500/40 text-white shadow-2xl shadow-amber-500/10 flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
              <div className="text-xs font-semibold">{toastMessage}</div>
              <button
                type="button"
                onClick={() => setToastMessage(null)}
                className="text-slate-400 hover:text-white ml-2 text-sm font-bold"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

